import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import {
  FormControl,
  Select,
  Stack,
  Typography,
  type SelectChangeEvent,
  type SelectProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { mergeSx } from '../../../../utils/styles';

const styles = {
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
    gap: { xs: 1, md: 1.5 },
    minWidth: 0,
    flexWrap: 'nowrap',
  },
  control: {
    minWidth: 0,
    maxWidth: '100%',
  },
  label: {
    color: 'text.secondary',
    typography: 'caption',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    lineHeight: 1,
    minWidth: 0,
    flexShrink: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  select: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 0,
      width: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  menuPaper: {
    zIndex: (theme: Theme) => theme.zIndex.modal + 100,
  },
} as const;

const selectDisplayStyle = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

type NeobetSelectFieldProps = {
  label?: string;
  value: string;
  onChange: SelectProps<string>['onChange'];
  children: ReactNode;
  renderValue?: SelectProps<string>['renderValue'];
  inputProps?: SelectProps<string>['inputProps'];
  showLabel?: boolean;
  testId?: string;
  rootSx?: SxProps<Theme>;
  controlSx?: SxProps<Theme>;
  selectSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  selectDisplayProps?: SelectProps<string>['SelectDisplayProps'];
  IconComponent?: SelectProps<string>['IconComponent'];
  size?: SelectProps<string>['size'];
};

const NeobetSelectField = ({
  label,
  value,
  onChange,
  children,
  renderValue,
  inputProps,
  showLabel = true,
  testId,
  rootSx,
  controlSx,
  selectSx,
  labelSx,
  selectDisplayProps,
  IconComponent = KeyboardArrowDownRoundedIcon,
  size = 'small',
}: NeobetSelectFieldProps) => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const [menuWidth, setMenuWidth] = useState<number>();

  const updateMenuWidth = useCallback(() => {
    const nextWidth = controlRef.current?.getBoundingClientRect().width;

    if (nextWidth) {
      setMenuWidth(nextWidth);
    }
  }, []);

  const handleControlRef = useCallback(
    (node: HTMLDivElement | null) => {
      controlRef.current = node;
      updateMenuWidth();
    },
    [updateMenuWidth]
  );

  return (
    <Stack sx={mergeSx(styles.root, rootSx)}>
      {showLabel && label ? (
        <Typography component='span' sx={mergeSx(styles.label, labelSx)}>
          {label}
        </Typography>
      ) : null}
      <FormControl ref={handleControlRef} size={size} sx={mergeSx(styles.control, controlSx)}>
        <Select
          value={value}
          onChange={onChange as (event: SelectChangeEvent<unknown>, child: ReactNode) => void}
          IconComponent={IconComponent}
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            marginThreshold: 0,
            slotProps: {
              paper: {
                style: {
                  minWidth: menuWidth,
                },
              },
            },
          }}
          onOpen={updateMenuWidth}
          size={size}
          renderValue={renderValue}
          SelectDisplayProps={{
            ...selectDisplayProps,
            style: {
              ...selectDisplayStyle,
              ...selectDisplayProps?.style,
            },
          }}
          inputProps={inputProps}
          sx={mergeSx(styles.select, selectSx)}
          data-testid={testId}
        >
          {children}
        </Select>
      </FormControl>
    </Stack>
  );
};

export default NeobetSelectField;
